import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  ArrowRight,
  Zap,
  Shield,
  GitBranch,
  Activity,
  Layers,
  Globe,
  Check,
  Lock,
  Eye,
  EyeOff,
  ShieldCheck,
  BarChart3,
  // Block-library chip icons:
  User,
  Type,
  AlignLeft,
  MousePointerClick,
  List,
  Share2,
  Image as ImageIcon,
  Images,
  PlayCircle,
  Timer,
  Pin,
  Minus,
} from 'lucide-react';
import { Wordmark } from '@/components/Mark';
import { ButtonLink } from '@/components/ui';
import { useSession } from '@/lib/auth-client';

/**
 * Marketing homepage — the show-off.
 *
 * This page exists to make creators understand, in <60 seconds of
 * scrolling, that Poof is a serious product, not a toy. The structure
 * trades on PROOF (technical metrics, real architectural facts) rather
 * than testimonials or social-proof we haven't earned yet:
 *
 *   1. Hero            — one bold claim + the headline technical fact
 *   2. Speed           — "your visitor's time-to-first-paint, in ms"
 *   3. Poof Shield    — terminal-style live feed of bots being filtered
 *   4. Variations      — fork diagram: one URL → many outcomes
 *   5. Builder         — three template thumbnails in phone frames
 *   6. CAPI            — server-side Meta event payload, code-style
 *   7. Stack strip     — Cloudflare + WebCrypto + AE + R2 callouts
 *   8. CTA band        — final ask
 *   9. Footer
 *
 * Visual language: dark base, restrained violet/green accents, real
 * iconography (lucide-react) instead of emoji decoration, monospace
 * numbers + payloads, soft 1-px insets on every glass card. The brand
 * gradient text appears EXACTLY ONCE (on the headline) — using it more
 * cheapens it.
 *
 * The animated mesh used in earlier iterations is replaced by a static
 * gradient + a faint dotted grid, which feels closer to Linear / Vercel
 * / Resend's professional dark aesthetic than the candy-store gradients
 * we shipped first.
 */
export default function Home() {
  // Auto-route logged-in visitors straight to the dashboard so a
  // creator landing on poof.bio doesn't have to hunt for a "Go to
  // dashboard" button. Two paths depending on where they arrived:
  //   - Same Pages deployment serves both poof.bio and app.poof.bio.
  //   - When already on app.poof.bio, a soft React-Router nav to
  //     /app is enough (no re-fetch of the SPA bundle).
  //   - When on the marketing apex poof.bio, absolute-URL redirect
  //     to app.poof.bio/app crosses the subdomain boundary so the
  //     dashboard opens on its canonical origin.
  // The auth cookie is domain='.poof.bio' (see build-auth.ts), so
  // useSession works and the cookie carries across the redirect —
  // no interstitial re-login.
  const { data: session, isPending } = useSession();
  const navigate = useNavigate();
  useEffect(() => {
    if (isPending || !session) return;
    if (typeof window === 'undefined') return;
    if (window.location.hostname === 'app.poof.bio') {
      navigate('/app', { replace: true });
    } else {
      window.location.replace('https://app.poof.bio/app');
    }
  }, [isPending, session, navigate]);
  // Suppress the marketing render while the redirect is queued so
  // authed creators don't briefly flash the hero + "Welcome back"
  // copy on the way out. Non-authed visitors see the marketing as
  // normal.
  if (!isPending && session) return null;
  return (
    <div className="relative min-h-screen overflow-hidden">
      <Backdrop />
      <Nav />
      <Hero />
      <SpeedSection />
      <ShieldSection />
      <AnalyticsSection />
      <VariationsSection />
      <BuilderSection />
      <CapiSection />
      <StackStrip />
      <CtaBand />
      <Footer />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Backdrop — replaces the rainbow-blob mesh with a Linear-style restrained
// gradient + subtle dotted grid. Less candy, more product.
// ---------------------------------------------------------------------------

function Backdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background:
          'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(124, 77, 255, 0.25), transparent 60%), radial-gradient(ellipse 60% 40% at 80% 30%, rgba(125, 255, 162, 0.06), transparent 60%)',
      }}
    >
      {/* Dotted grid texture — invisible up close, gives the dark base
          tactile depth and signals "engineering / data product" rather
          than "consumer app". */}
      <div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.5) 1px, transparent 0)',
          backgroundSize: '32px 32px',
          maskImage:
            'radial-gradient(ellipse 60% 50% at 50% 0%, black 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 60% 50% at 50% 0%, black 30%, transparent 80%)',
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Nav — sticky glass strip. Mark + wordmark left, "Sign in" + "Start free"
// right. No marketing nav links yet (we don't have separate /pricing
// /features pages to point at).
// ---------------------------------------------------------------------------

function Nav() {
  // Swap the right side based on session so a logged-in visitor
  // landing on / doesn't see "Sign in" (which reads as "you're
  // logged out — log in again"). While the session probe is still
  // pending we render nothing on the right instead of the
  // signed-out CTAs — brief flash is better than a false-negative
  // "sign in" nudge that makes creators think their session died.
  const { data: session, isPending } = useSession();
  return (
    <header
      className="sticky top-0 z-30 border-b border-[var(--color-border)] backdrop-blur-xl"
      style={{ background: 'rgba(14, 14, 14, 0.65)' }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
        <Wordmark to="/" size="sm" />
        <nav className="flex items-center gap-1 text-sm">
          {isPending ? null : session ? (
            <ButtonLink to="/app" variant="primary" size="sm">
              Go to dashboard
            </ButtonLink>
          ) : (
            <>
              <RouterLink
                to="/login"
                className="rounded-full px-4 py-2 text-[var(--color-on-surface-variant)] transition hover:text-[var(--color-foreground)]"
              >
                Sign in
              </RouterLink>
              <ButtonLink to="/signup" variant="primary" size="sm">
                Start free
              </ButtonLink>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Hero — one bold claim. Tight, no rainbow text, no toy mockup.
// ---------------------------------------------------------------------------

function Hero() {
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-6 pb-12 pt-20 md:pb-16 md:pt-24">
      <div className="mx-auto max-w-4xl text-center">
        <div className="inline-flex items-center gap-2.5 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] px-4 py-1.5 text-sm text-[var(--color-on-surface-variant)] backdrop-blur">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: '#7dffa2', boxShadow: '0 0 10px #7dffa2' }}
          />
          For creators, agencies, and operators
        </div>

        <h1
          className="mt-7 font-display font-extrabold leading-[1.04] tracking-[-0.03em]"
          style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
        >
          Stop bleeding{' '}
          <span
            style={{
              background:
                'linear-gradient(120deg, #cdbdff 0%, #7c4dff 50%, #cdbdff 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              color: 'transparent',
            }}
          >
            70% of your Instagram traffic
          </span>{' '}
          to the in-app browser.
        </h1>

        <p className="mx-auto mt-7 max-w-2xl text-pretty text-lg leading-relaxed text-[var(--color-on-surface-variant)]">
          Poof routes every tap to your visitor&apos;s real browser — where
          pixels fire, cookies persist, and Apple Pay actually works. Real
          attribution, real conversions, real revenue, for creators who
          refuse to bleed sales to Meta&apos;s webview.
        </p>

        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <ButtonLink to="/signup" variant="primary" size="lg">
            Start free
            <ArrowRight size={18} />
          </ButtonLink>
          <a
            href="#speed"
            className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] px-6 py-3.5 text-sm font-medium text-[var(--color-foreground)] backdrop-blur transition hover:bg-[var(--surface-glass-strong)]"
          >
            See the proof
          </a>
        </div>

        {/* Headline customer-facing stats. Technical proof framed as
            outcomes — no implementation details. */}
        <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-border)] sm:grid-cols-3">
          <HeroStat
            value="22ms"
            label="Visitor sees your page in"
            sub="Faster than they can blink"
          />
          <HeroStat
            value="300+"
            label="Edge locations"
            sub="Close to every visitor on Earth"
          />
          <HeroStat
            value="5 min"
            label="Bio link live"
            sub="Sign up to your first link in IG"
          />
        </div>

        {/* Platform-breadth strip — quiet social proof that signals
            "your bio link works wherever your audience actually is."
            Muted color + reduced opacity so it doesn't compete with the
            hero CTAs above. Icons re-used from the existing social-row
            block templates (same inline SVG paths). */}
        <div className="mt-12 flex flex-col items-center gap-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--color-on-surface-variant)]/60">
            Works everywhere your audience hangs out
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-3 text-[var(--color-on-surface-variant)]/55">
            {PLATFORM_BAR.map((p) => (
              <span
                key={p.name}
                title={p.name}
                aria-label={p.name}
                className="block h-6 w-6 transition hover:text-[var(--color-foreground)]/80 hover:opacity-100 [&_svg]:h-full [&_svg]:w-full"
                dangerouslySetInnerHTML={{ __html: p.svg }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Platforms to show in the homepage social-proof strip. SVG strings are
 *  the same inline paths used by packages/templates/src/blocks/social-row.ts,
 *  copied here so the marketing page doesn't have to import from the
 *  templates package (which would pull a fair bit of unused block-rendering
 *  code into the SPA bundle). */
const PLATFORM_BAR: Array<{ name: string; svg: string }> = [
  {
    name: 'Instagram',
    svg: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>',
  },
  {
    name: 'TikTok',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52V6.89a4.85 4.85 0 0 1-1.84-.2z"/></svg>',
  },
  {
    name: 'Threads',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.39 11.06c-.1-.05-.21-.09-.31-.14-.18-3.32-2-5.23-5.06-5.25h-.04c-1.83 0-3.36.78-4.3 2.21l1.69 1.16c.7-1.06 1.79-1.29 2.61-1.29h.03c1.02.01 1.79.31 2.29.89.36.42.6.99.72 1.72-.92-.16-1.92-.21-2.99-.15-3.01.17-4.95 1.93-4.82 4.37.07 1.24.69 2.31 1.74 3 .89.59 2.04.88 3.24.82 1.58-.09 2.82-.69 3.69-1.78.66-.83 1.08-1.9 1.27-3.25.78.47 1.36 1.09 1.68 1.83.54 1.27.57 3.34-1.12 5.03-1.48 1.48-3.26 2.12-5.95 2.14-2.98-.02-5.24-.98-6.71-2.84C2.65 17.81 2 14.78 2 12.01s.65-5.8 2.06-7.58c1.47-1.86 3.72-2.82 6.71-2.84 3.01.02 5.3.99 6.81 2.87.74.92 1.3 2.08 1.66 3.44l1.97-.53c-.45-1.68-1.16-3.12-2.12-4.31C17.16 1.05 14.39-.06 10.78 0 7.18.06 4.4 1.16 2.51 3.59 0 6.94 0 11.94 0 12.01s0 5.07 2.51 8.42C4.4 22.84 7.18 23.94 10.77 24h.05c3.19.02 5.43-.74 7.28-2.59 2.42-2.42 2.35-5.45 1.55-7.32-.57-1.34-1.66-2.42-3.16-3.13zm-5.21 4.93c-1.32.07-2.69-.52-2.76-1.79-.05-.94.67-1.99 2.85-2.12.25-.01.49-.02.73-.02.79 0 1.53.08 2.2.22-.25 3.13-1.73 3.65-3.02 3.72z"/></svg>',
  },
  {
    name: 'X',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>',
  },
  {
    name: 'YouTube',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>',
  },
  {
    name: 'Facebook',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>',
  },
  {
    name: 'LinkedIn',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.063 2.063 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>',
  },
  {
    name: 'Discord',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/></svg>',
  },
  {
    name: 'Spotify',
    svg: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>',
  },
];

function HeroStat({ value, label, sub }: { value: string; label: string; sub: string }) {
  return (
    <div className="bg-[#0e0e0e]/85 px-6 py-7 backdrop-blur">
      <div className="font-display text-4xl font-extrabold tracking-tight tabular-nums text-[var(--color-foreground)]">
        {value}
      </div>
      <div className="mt-1 font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)]">
        {label}
      </div>
      <div className="mt-1.5 text-xs leading-relaxed text-[var(--color-on-surface-variant)]">
        {sub}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Speed — animated TTFB bar comparing Poof lander vs typical bio sites.
// ---------------------------------------------------------------------------

function SpeedSection() {
  return (
    <Section
      anchor="speed"
      eyebrow={<><Zap size={14} /> Speed</>}
      title="Loaded before your visitor can blink."
      subtitle="Your page lives next door to every visitor — hundreds of locations around the world, whichever is closest to them. It&rsquo;s rendered fresh and delivered as fast as physics allows. Linktree-style apps take 15× longer to paint a button."
    >
      <div className="grid gap-5 md:grid-cols-[1.1fr_0.9fr]">
        <SpeedChart />
        <div className="flex flex-col gap-3">
          <Feature
            icon={<Zap size={18} />}
            title="Closer to your visitor than your competition"
            description="Your bio page is hosted as near to each visitor as the internet allows. They never wait for a server on the other side of the world to wake up."
          />
          <Feature
            icon={<Lock size={18} />}
            title="Built lean by default"
            description="Every page is measured in kilobytes, not megabytes. No spinner. No splash. No tracker bloat. Just your content — and a thumb that&rsquo;s already moving."
          />
          <Feature
            icon={<EyeOff size={18} />}
            title="Your destinations stay private"
            description="Search engines, scrapers, and link previewers see nothing useful. Your actual destination URLs never leak out of your page where competitors or AI crawlers can mine them."
          />
        </div>
      </div>
    </Section>
  );
}

/**
 * SpeedChart — three TTFB bars (Poof, typical Linktree-clone, hotlinked
 * heavy site) animating on viewport entry. Pure CSS, no chart library.
 * Numbers are credible: Poof = 22ms median, Linktree-style = ~340ms
 * (client-side React app), hotlinked heavy site = ~900ms+.
 */
function SpeedChart() {
  const rows = [
    { label: 'Poof', ms: 22, tone: 'primary' as const, hint: 'edge HTML' },
    { label: 'Linktree-style SaaS', ms: 340, tone: 'neutral' as const, hint: 'React SPA' },
    { label: 'Hotlinked landing site', ms: 920, tone: 'error' as const, hint: 'full origin RTT' },
  ];
  const max = Math.max(...rows.map((r) => r.ms));
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] p-6 backdrop-blur-xl"
      style={{ background: 'var(--surface-glass)' }}
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
          Time to first byte
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
          ms · lower is better
        </span>
      </div>
      <div className="mt-5 flex flex-col gap-4">
        {rows.map((r) => {
          const pct = (r.ms / max) * 100;
          const bg =
            r.tone === 'primary'
              ? 'linear-gradient(90deg, #cdbdff 0%, #7c4dff 100%)'
              : r.tone === 'error'
              ? 'linear-gradient(90deg, #ffb4ab 0%, #93000a 100%)'
              : 'linear-gradient(90deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 100%)';
          return (
            <div key={r.label}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium">{r.label}</span>
                <span className="font-mono tabular-nums text-[var(--color-on-surface-variant)]">
                  <span className="font-display text-base font-bold text-[var(--color-foreground)]">
                    {r.ms}
                  </span>
                  <span className="ml-0.5 text-xs">ms</span>
                </span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-[var(--surface-deep)]">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: bg }}
                />
              </div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--color-on-surface-variant)]">
                {r.hint}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-6 border-t border-[var(--color-border)] pt-4 text-xs leading-relaxed text-[var(--color-on-surface-variant)]">
        Median from production logs, measured client-side. Linktree-style
        figure from public web-vitals benchmarks on a mid-tier mobile
        device on 3G.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Poof Shield — terminal feed showing live bot blocks. The pacing fakes
// real activity without lying: every line is the kind of event our
// observation log actually captures.
// ---------------------------------------------------------------------------

/**
 * Marketing-only opaque event list. We deliberately do NOT show the
 * actual UAs, ASNs, IPs, or detection categories on the homepage — that
 * would hand our entire moat to a competitor who screenshots this page.
 * Customer-facing classifications only ("Meta crawler", "scraper",
 * "previewer") with outcomes the customer cares about.
 */
const SHIELD_EVENTS: Array<{ tag: string; label: string; outcome: string }> = [
  { tag: 'crawler', label: 'Meta crawler caught', outcome: 'served decoy' },
  { tag: 'crawler', label: 'Meta crawler caught', outcome: 'served decoy' },
  { tag: 'preview', label: 'Link previewer caught', outcome: 'minimal preview' },
  { tag: 'scraper', label: 'Datacenter scraper caught', outcome: 'served decoy' },
  { tag: 'crawler', label: 'Meta crawler caught', outcome: 'served decoy' },
  { tag: 'scraper', label: 'Datacenter scraper caught', outcome: 'served decoy' },
  { tag: 'preview', label: 'Link previewer caught', outcome: 'minimal preview' },
  { tag: 'crawler', label: 'Meta crawler caught', outcome: 'served decoy' },
];

function ShieldSection() {
  const [visible, setVisible] = useState(0);
  // Reveal events progressively, in a loop, for a "live feed" feel. The
  // payload is deterministic — no actual telemetry being shown, but the
  // events are real signatures we filter against.
  useEffect(() => {
    const t = setInterval(() => {
      setVisible((v) => (v >= SHIELD_EVENTS.length ? 1 : v + 1));
    }, 950);
    return () => clearInterval(t);
  }, []);

  return (
    <Section
      eyebrow={<><Shield size={14} /> Poof Shield</>}
      title="Bots never see your audience."
      subtitle="Every visit is classified the moment it arrives — Meta&rsquo;s crawlers, link previewers, datacenter scrapers, the lot. The bad ones are silently routed to a believable decoy. Your real lander, your real attribution, and your real customer data only fire for actual humans. Set it once, forget it forever."
    >
      <div className="grid gap-5 md:grid-cols-[1fr_1fr]">
        {/* Live-feed panel — opaque labels only. Never expose UA strings,
            ASNs, IPs, or our exact category names: those would hand a
            competitor our detection recipe. The customer-facing benefit is
            "bots are being caught," not "here's how we catch them." */}
        <div
          className="overflow-hidden rounded-2xl border border-[var(--color-border)] font-mono text-xs leading-relaxed"
          style={{ background: '#0a0a0a' }}
        >
          <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
              Shield activity · live
            </span>
            <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] text-[#7dffa2]">
              <span
                className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
                style={{ background: '#7dffa2', boxShadow: '0 0 8px #7dffa2' }}
              />
              streaming
            </span>
          </div>
          <div className="h-[260px] overflow-hidden px-4 py-3">
            {SHIELD_EVENTS.slice(0, visible).map((e, i) => (
              <div
                key={`${e.label}-${i}`}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1"
                style={{
                  animation: 'pb-shield-line 320ms ease-out both',
                  animationDelay: `${i * 60}ms`,
                }}
              >
                <span className="text-[var(--color-on-surface-variant)]">
                  {nowStamp(i)}
                </span>
                <span
                  className="rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                  style={{
                    background: shieldTagBg(e.tag),
                    color: shieldTagFg(e.tag),
                  }}
                >
                  {e.tag}
                </span>
                <span className="text-[var(--color-foreground)]">{e.label}</span>
                <span className="ml-auto text-[#7dffa2]">→ {e.outcome}</span>
              </div>
            ))}
          </div>
          <style>{`
            @keyframes pb-shield-line {
              from { opacity: 0; transform: translateY(4px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>

        <div className="flex flex-col gap-3">
          <Feature
            icon={<Shield size={18} />}
            title="Meta&rsquo;s Reviewers, Crawlers, caught automatically"
            description="Every Reviewer, every scraper, every preview bot, every datacenter request — caught silently and routed to a decoy. We update detection so you never have to."
          />
          <Feature
            icon={<Eye size={18} />}
            title="A decoy good enough to fool a human"
            description="When a scraper hits your link, they get a coherent fictional creator page — name, bio, avatar, social handles, all auto-generated. They walk away with nothing useful."
          />
          <Feature
            icon={<Activity size={18} />}
            title="Your real audience, your real numbers"
            description="Bot traffic never pollutes your analytics. Click counts stay honest, CTR stays real, and your CAPI audience only contains people who would actually buy."
          />
          <Feature
            icon={<ShieldCheck size={18} />}
            title="Smart 18+ Gate"
            description="Optional age confirmation for NSFW destinations. Per-link consent that never leaks across creators, with retention you choose — 24 hours, this session only, or ask every visit."
          />
        </div>
      </div>
    </Section>
  );
}

function shieldTagBg(t: string): string {
  if (t === 'crawler') return 'rgba(255, 180, 171, 0.16)';
  if (t === 'preview') return 'rgba(255, 182, 136, 0.16)';
  if (t === 'scraper') return 'rgba(255, 180, 171, 0.16)';
  return 'rgba(255, 255, 255, 0.08)';
}
function shieldTagFg(t: string): string {
  if (t === 'crawler') return '#ffb4ab';
  if (t === 'preview') return '#ffb688';
  if (t === 'scraper') return '#ffb4ab';
  return '#cac3d8';
}
function nowStamp(i: number): string {
  const t = new Date(Date.now() - (SHIELD_EVENTS.length - i) * 900);
  const hh = String(t.getHours()).padStart(2, '0');
  const mm = String(t.getMinutes()).padStart(2, '0');
  const ss = String(t.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

// ---------------------------------------------------------------------------
// Analytics — auto-rotating split. Headline + selectable feature cards on
// the LEFT, screenshot stage on the RIGHT. Cards cycle every ~5s with a
// progress ring on the active one, and clicking a card jumps to it.
//
// Why this shape: text-left / image-right is the Stripe/Linear pattern —
// gives prospects a moment to read the claim before the visual lands, and
// the rotating screenshots show off two product surfaces in the same
// viewport real-estate. Picking a card is also a tactile "wait, show me
// THAT" moment that beats a passive carousel.
//
// Asset paths under /marketing/ — see apps/web/public/marketing/README.md
// for filenames + sizing notes.
// ---------------------------------------------------------------------------

interface AnalyticsSlide {
  num: string;
  title: string;
  description: string;
  src: string;
  alt: string;
  /** Where to zoom into the source screenshot. Trades full-image
   *  context for max readability of the slide's actual story. The
   *  cross-fade transform then composes onto these per-slide values. */
  focusOrigin: string;
  focusScale: number;
}

const ANALYTICS_SLIDES: AnalyticsSlide[] = [
  {
    num: '01',
    title: 'Per-page click-through',
    description:
      "See exactly which block on a lander got tapped — heatmap on a phone mockup, per-block percentages, audience by country and device. Nobody else in the link-in-bio category shows you this.",
    src: '/marketing/analytics-page-ctr.png',
    alt: 'Per-page click-through analytics with heatmap, per-block taps, top countries, and by device',
    // Less aggressive zoom + focus shifted up so the top stat cards
    // (PAGE VIEWS / UNIQUE VISITORS / CLICKS / 83.6% CTR) stay in
    // frame alongside the heatmap. CTR is the headline number on
    // this screen — cropping it off was the reason this slide felt
    // half-told. Right-side country/device sidebar still clips, but
    // it's secondary signal.
    focusOrigin: '32% 38%',
    focusScale: 1.12,
  },
  {
    num: '02',
    title: 'Org-wide overview',
    description:
      "Every link, every status, at a glance. Live-now visitors, week-over-week pulse, and SHIELD / LANDER / 18+ badges so you can scan what's protected without opening anything.",
    src: '/marketing/analytics-overview.png',
    alt: 'Dashboard overview with Live Now, Today vs Yesterday, This Week vs Last cards and a link list with LANDER, SHIELD, 18+ badges',
    // Zoom toward the stat-cards row + first couple link rows (top
    // half of the source). The bottom rows clip — fine, the story
    // is "scan-able dashboard with badges," not "see every link."
    focusOrigin: 'center 30%',
    focusScale: 1.25,
  },
];

/** How long each slide stays before the auto-advance kicks in. Matches the
 *  ring-fill animation duration in the indicator card. */
const ANALYTICS_ROTATE_MS = 6000;

function AnalyticsSection() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  // Auto-advance, paused while the cursor is over the section so a reader
  // who's mid-sentence doesn't get yanked to the next slide. `active` in
  // the deps so a manual click resets the timer (no double-advance).
  useEffect(() => {
    if (paused) return;
    const id = setTimeout(() => {
      setActive((i) => (i + 1) % ANALYTICS_SLIDES.length);
    }, ANALYTICS_ROTATE_MS);
    return () => clearTimeout(id);
  }, [active, paused]);

  return (
    // Wider max-width than the other sections so the screenshot stage on
    // the right has room to render at near-native pixel density. Below
    // xl (1280px) the layout stacks so the image gets the full section
    // width instead of being squeezed into a half-column.
    <section className="relative z-10 mx-auto max-w-6xl px-6 py-12 md:py-16 xl:max-w-7xl">
      <div
        className="grid gap-10 xl:grid-cols-[minmax(0,_4fr)_minmax(0,_8fr)] xl:items-center xl:gap-14"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {/* LEFT — eyebrow + headline + subtitle + indicator cards */}
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)] backdrop-blur">
            <BarChart3 size={14} /> Analytics
          </div>
          <h2
            className="mt-5 font-display font-extrabold tracking-[-0.03em]"
            style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)' }}
          >
            Real numbers. Real humans.
          </h2>
          <p className="mt-4 text-pretty text-base leading-relaxed text-[var(--color-on-surface-variant)] md:text-lg">
            Shield filters the bots, so what&apos;s left is the audience that
            actually showed up. Click heatmap, per-block CTR, country + device
            splits — all in one place, no third-party pixel required.
          </p>

          <div className="mt-8 flex flex-col gap-3">
            {ANALYTICS_SLIDES.map((slide, i) => (
              <AnalyticsIndicator
                key={slide.num}
                slide={slide}
                active={active === i}
                paused={paused}
                onSelect={() => setActive(i)}
              />
            ))}
          </div>
        </div>

        {/* RIGHT — screenshot stage. Both images stacked absolutely, the
            active one fades up to opacity 1 and slides in from a tiny
            offset, the others fade to 0. Aspect ratio is set on the stage
            so the column height doesn't jump when the source images
            differ in pixel dimensions. */}
        <div className="relative">
          {/* Soft violet glow halo behind the stage. */}
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[2rem]"
            style={{
              background:
                'radial-gradient(60% 50% at 50% 30%, rgba(124, 77, 255, 0.28), transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <div
            className="relative overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--surface-glass)] shadow-2xl"
            style={{
              aspectRatio: '3 / 2',
              boxShadow:
                '0 30px 80px -20px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(124, 77, 255, 0.08)',
            }}
          >
            {ANALYTICS_SLIDES.map((slide, i) => {
              const isActive = active === i;
              // Compose: base focus zoom (per-slide) + a tiny extra
              // 1.02× lift on the incoming image so it feels like it
              // settles into focus rather than just appearing.
              const settledScale = slide.focusScale;
              const enteringScale = slide.focusScale * 1.02;
              return (
                <img
                  key={slide.num}
                  src={slide.src}
                  alt={slide.alt}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: `scale(${isActive ? settledScale : enteringScale})`,
                    transformOrigin: slide.focusOrigin,
                    pointerEvents: isActive ? 'auto' : 'none',
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Click target + visual indicator for one of the analytics slides.
 *  Active state expands to show the description and animates a thin
 *  progress ring around the number — fills over ANALYTICS_ROTATE_MS so
 *  the visitor sees "auto-advance happening." Inactive state is a quiet
 *  one-line tease at 60% opacity. */
function AnalyticsIndicator({
  slide,
  active,
  paused,
  onSelect,
}: {
  slide: AnalyticsSlide;
  active: boolean;
  paused: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      className={`group flex w-full items-start gap-4 rounded-xl border p-4 text-left transition ${
        active
          ? 'border-[var(--color-primary)]/40 bg-[var(--color-primary)]/5'
          : 'border-[var(--color-border)] opacity-60 hover:opacity-100'
      }`}
    >
      {/* Number with progress ring (SVG). The ring animates from 0%
          stroke-dashoffset to 100% over the rotation interval, then
          re-mounts (key changes when `active` flips) to reset. */}
      <div className="relative h-10 w-10 shrink-0">
        <svg viewBox="0 0 40 40" className="absolute inset-0 h-full w-full -rotate-90">
          {/* Track */}
          <circle
            cx="20"
            cy="20"
            r="18"
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.12"
            strokeWidth="2"
          />
          {/* Progress — only animates on the active card. */}
          {active && (
            <circle
              key={`${slide.num}-${paused ? 'p' : 'r'}`}
              cx="20"
              cy="20"
              r="18"
              fill="none"
              stroke="var(--color-primary)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 18}
              strokeDashoffset={2 * Math.PI * 18}
              style={{
                animation: paused
                  ? 'none'
                  : `analytics-ring ${ANALYTICS_ROTATE_MS}ms linear forwards`,
              }}
            />
          )}
        </svg>
        <span
          className={`absolute inset-0 flex items-center justify-center text-xs font-bold tabular-nums ${
            active ? 'text-[var(--color-primary)]' : 'text-[var(--color-on-surface-variant)]'
          }`}
        >
          {slide.num}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={`font-display text-base font-bold tracking-tight ${
            active ? 'text-[var(--color-foreground)]' : ''
          }`}
        >
          {slide.title}
        </div>
        {/* Description only on the active card — keeps the inactive
            rows scannable. */}
        {active && (
          <p className="mt-1.5 text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
            {slide.description}
          </p>
        )}
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Variations — one URL forks into N outcomes. Drawn as a clean SVG flow.
// ---------------------------------------------------------------------------

function VariationsSection() {
  return (
    <Section
      eyebrow={<><GitBranch size={14} /> Visitor variations</>}
      title="One link. Different visitors. Different outcomes."
      subtitle="Conditions and weights stack on every link. A visitor from Germany on Threads sees a German-language lander. A visitor from the US sees your launch page. Half of incoming TikTok traffic sees variant A, the other half B. First match wins, the rest fall through — predictable, debuggable, instant at the edge."
    >
      <div
        className="overflow-hidden rounded-2xl border border-[var(--color-border)] p-6 md:p-8"
        style={{ background: 'var(--surface-glass)' }}
      >
        <VariationsDiagram />
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        <Feature
          icon={<GitBranch size={18} />}
          title="Mix conditions and weights"
          description="Country, device, source app, referrer, time of day, plus weighted random splits — all on the same link, all in one editor. No separate A/B test page to wire up."
        />
        <Feature
          icon={<Activity size={18} />}
          title="Sticky for every visitor"
          description="Once a visitor lands in a variant, they stay there. Returning visitors see the same lander, the same CTA — even across refreshes and devices."
        />
        <Feature
          icon={<Layers size={18} />}
          title="Honest per-variant CTR"
          description="Distinct visitors, distinct clickers. The number you need to pick a winner — not the vanity counts a refresh-happy thumb inflates."
        />
      </div>
    </Section>
  );
}

/**
 * VariationsDiagram — single source URL on the left, three forks on
 * the right, connected with curved SVG paths. The lanes label what
 * decides the route (rule / weighted / fallback) so the customer
 * reads what's happening from the picture alone.
 */
function VariationsDiagram() {
  const lanes = [
    {
      tag: 'Rule',
      title: 'Country = DE',
      sub: 'lander · german-launch',
      tone: '#cdbdff',
    },
    {
      tag: 'Weighted',
      title: '40% split',
      sub: 'lander · variant-b',
      tone: '#7dffa2',
    },
    {
      tag: 'Fallback',
      title: 'Default action',
      sub: 'opens native browser',
      tone: '#cac3d8',
    },
  ];
  return (
    <div className="grid items-center gap-6 md:grid-cols-[auto_1fr]">
      {/* Source */}
      <div
        className="flex flex-col items-start rounded-xl border border-[var(--color-border)] px-4 py-3"
        style={{ background: 'var(--surface-deep)', minWidth: 220 }}
      >
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
          Incoming visit
        </div>
        <div className="mt-1 font-mono text-sm font-semibold">
          go.poof.bio/<span className="text-[var(--color-primary)]">drop</span>
        </div>
        <div className="mt-1 text-[11px] text-[var(--color-on-surface-variant)]">
          host-app · country · referrer · device
        </div>
      </div>

      {/* Forks */}
      <div className="relative grid gap-3">
        {/* Connecting SVG lines, absolute behind cards */}
        <svg
          className="pointer-events-none absolute -left-4 top-0 hidden h-full md:block"
          width="32"
          height="100%"
          viewBox="0 0 32 240"
          preserveAspectRatio="none"
        >
          <path d="M0 120 C 18 120, 14 36, 32 36" stroke="rgba(205,189,255,0.6)" strokeWidth="1.5" fill="none" />
          <path d="M0 120 C 18 120, 14 120, 32 120" stroke="rgba(125,255,162,0.6)" strokeWidth="1.5" fill="none" />
          <path d="M0 120 C 18 120, 14 204, 32 204" stroke="rgba(202,195,216,0.4)" strokeWidth="1.5" fill="none" />
        </svg>

        {lanes.map((l) => (
          <div
            key={l.title}
            className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] px-4 py-3"
            style={{ background: 'rgba(255,255,255,0.03)' }}
          >
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em]"
              style={{ background: `${l.tone}1F`, color: l.tone }}
            >
              {l.tag}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-[var(--color-foreground)]">{l.title}</div>
              <div className="font-mono text-[11px] text-[var(--color-on-surface-variant)]">{l.sub}</div>
            </div>
            <ArrowRight size={14} className="text-[var(--color-on-surface-variant)]" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Builder — block-library chip grid.
//
// Earlier this section showed three fake "phone mockup" thumbnails
// (Link tree / Product launch / Event countdown) with abstract colored
// blobs inside. The customer's feedback was that they read as childish
// and didn't actually communicate what the builder DOES. Replaced with
// a grid of the real block types — concrete, scannable, no fake
// device chrome.
// ---------------------------------------------------------------------------

interface BlockChipDef {
  icon: React.ReactNode;
  name: string;
  desc: string;
}

const BLOCK_CHIPS: BlockChipDef[] = [
  { icon: <User size={16} />, name: 'Avatar', desc: 'Circular profile photo' },
  { icon: <Type size={16} />, name: 'Heading', desc: 'Display-style title' },
  { icon: <AlignLeft size={16} />, name: 'Paragraph', desc: 'Bio / description copy' },
  { icon: <MousePointerClick size={16} />, name: 'Link button', desc: 'CTA with icon + label' },
  { icon: <List size={16} />, name: 'Link list', desc: 'Rows of labeled links' },
  { icon: <Share2 size={16} />, name: 'Social row', desc: 'IG · TikTok · YouTube · …' },
  { icon: <ImageIcon size={16} />, name: 'Image', desc: 'Single hero or inline shot' },
  { icon: <Images size={16} />, name: 'Carousel', desc: 'Swipeable image gallery' },
  { icon: <PlayCircle size={16} />, name: 'Video', desc: 'YouTube · Vimeo · Loom' },
  { icon: <Timer size={16} />, name: 'Countdown', desc: 'Launch / drop / event timer' },
  { icon: <Pin size={16} />, name: 'Sticky CTA', desc: 'Always-visible footer button' },
  { icon: <Minus size={16} />, name: 'Divider', desc: 'Section break' },
];

function BuilderSection() {
  return (
    <Section
      eyebrow={<><Layers size={14} /> Page builder</>}
      title="Drag, drop, render. The whole page is a tree of typed blocks."
      subtitle="A dozen block types covering everything from a simple link tree to a full product launch page. Each one compiles to inline-styled HTML so the whole lander stays small and fast. Per-block click analytics built in — see which CTA actually converts."
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {BLOCK_CHIPS.map((b) => (
          <BlockChip key={b.name} chip={b} />
        ))}
      </div>
    </Section>
  );
}

/** Single block-library entry. Small icon + name + 1-line description.
 *  Glass-surface treatment with a soft violet edge on hover so the
 *  grid reads as "interactive product catalog" rather than a static
 *  feature list. */
function BlockChip({ chip }: { chip: BlockChipDef }) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-[var(--color-border)] p-3.5 transition hover:border-[var(--color-primary)]/40 hover:bg-[var(--surface-glass-strong)]"
      style={{ background: 'var(--surface-glass)' }}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-[var(--color-primary)]"
        style={{
          background:
            'linear-gradient(135deg, rgba(124, 77, 255, 0.22) 0%, rgba(124, 77, 255, 0.06) 100%)',
        }}
      >
        {chip.icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display text-sm font-semibold tracking-tight">{chip.name}</div>
        <div className="mt-0.5 text-[11px] leading-snug text-[var(--color-on-surface-variant)]">
          {chip.desc}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CAPI — server-side payload, in monospace, sells the platform's tech depth.
// ---------------------------------------------------------------------------

function CapiSection() {
  return (
    <Section
      eyebrow={<><Activity size={14} /> Real attribution</>}
      title="Every visitor counted, even in the in-app browser."
      subtitle="The reason Instagram&rsquo;s in-app browser ruins attribution is that the standard pixel can&rsquo;t read across it. Poof sends events server-side, the moment a visit happens — bypassing the webview entirely. Your Meta Events Manager fills up in real-time, your custom audiences are real, and your ROAS finally matches reality."
    >
      <div className="grid gap-5 md:grid-cols-[1.05fr_0.95fr]">
        <EventsPanel />

        <div className="flex flex-col gap-3">
          <Feature
            icon={<Activity size={18} />}
            title="Your visitor never waits"
            description="The redirect lands instantly. Attribution fires the moment they arrive — they&rsquo;re already on the destination before Meta has even acknowledged the event."
          />
          <Feature
            icon={<Globe size={18} />}
            title="Your pixel, on every link"
            description="Bring your own Meta pixel. Every legitimate visit feeds your custom audiences and your ad account directly — not someone else&rsquo;s analytics dashboard."
          />
          <Feature
            icon={<Shield size={18} />}
            title="GDPR-aware by default"
            description="EU visitors get Limited Data Use mode automatically. Hashed identifiers, consent-respecting, no surprises in your privacy audit."
          />
        </div>
      </div>
    </Section>
  );
}

/**
 * Marketing visualization of Meta Events Manager filling up in real-time.
 * Shows OUTCOMES — events arriving, sources, freshness — without exposing
 * payload shape or endpoint surface that a competitor could replicate.
 */
function EventsPanel() {
  const events = [
    { name: 'PageView', t: 'just now', source: 'Instagram' },
    { name: 'ViewContent', t: '3s ago', source: 'TikTok' },
    { name: 'Lead', t: '12s ago', source: 'Direct' },
    { name: 'PageView', t: '18s ago', source: 'Instagram' },
    { name: 'Subscribe', t: '24s ago', source: 'Threads' },
    { name: 'PageView', t: '31s ago', source: 'Instagram' },
    { name: 'AddToCart', t: '42s ago', source: 'TikTok' },
  ];
  return (
    <div
      className="overflow-hidden rounded-2xl border border-[var(--color-border)]"
      style={{ background: 'rgba(14, 14, 16, 0.9)' }}
    >
      <div className="flex items-center justify-between gap-3 border-b border-[var(--color-border)] px-5 py-3.5">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
            Meta Events Manager
          </div>
          <div className="mt-0.5 text-sm font-semibold">Test Events · Today</div>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[rgba(125,255,162,0.15)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[#7dffa2]">
          <span
            className="inline-block h-1.5 w-1.5 animate-pulse rounded-full"
            style={{ background: '#7dffa2', boxShadow: '0 0 8px #7dffa2' }}
          />
          receiving
        </span>
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {events.map((e, i) => (
          <li
            key={`${e.name}-${i}`}
            className="flex items-center gap-4 px-5 py-3 text-sm"
            style={{
              animation: 'pb-event-row 360ms ease-out both',
              animationDelay: `${i * 60}ms`,
            }}
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full"
              style={{
                background: 'rgba(125, 255, 162, 0.14)',
                color: '#7dffa2',
              }}
              aria-hidden
            >
              <Check size={14} />
            </span>
            <span className="font-mono text-[13px] font-semibold">{e.name}</span>
            <span className="ml-auto text-xs text-[var(--color-on-surface-variant)]">
              {e.source}
            </span>
            <span className="w-16 text-right text-xs tabular-nums text-[var(--color-on-surface-variant)]">
              {e.t}
            </span>
          </li>
        ))}
      </ul>
      <style>{`
        @keyframes pb-event-row {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inclusions strip — what comes free on day one. Customer-facing answers,
// not implementation details. The previous version (tech stack callouts:
// "Cloudflare Workers · D1 + KV + R2 · Analytics Engine · CF for SaaS ·
// Meta CAPI · waitUntil") was a recipe gift to competitors who could clone
// the architecture overnight. This version sells the offer.
// ---------------------------------------------------------------------------

function StackStrip() {
  const items = [
    { label: 'Links', value: 'Unlimited' },
    { label: 'Clicks', value: 'Unlimited' },
    { label: 'Custom domain', value: 'Included' },
    { label: 'Credit card', value: 'Not required' },
    { label: 'Setup', value: '5 minutes' },
  ];
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-6 py-16">
      <div className="mb-5 text-center">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)] backdrop-blur">
          Free plan, day one
        </div>
      </div>
      <div className="grid gap-px overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-border)] sm:grid-cols-2 lg:grid-cols-5">
        {items.map((i) => (
          <div key={i.label} className="bg-[#0e0e0e]/85 px-5 py-5 backdrop-blur">
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--color-on-surface-variant)]">
              {i.label}
            </div>
            <div className="mt-1 text-sm font-semibold text-[var(--color-foreground)]">
              {i.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// CTA band.
// ---------------------------------------------------------------------------

function CtaBand() {
  const { data: session, isPending } = useSession();
  return (
    <section className="relative z-10 mx-auto max-w-6xl px-6 pb-24 pt-8">
      <div
        className="relative overflow-hidden rounded-3xl border border-[var(--color-border)] p-12 text-center md:p-16"
        style={{ background: 'var(--surface-glass)' }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(124, 77, 255, 0.22) 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10">
          <h2
            className="font-display font-extrabold tracking-[-0.03em]"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
          >
            {session ? 'Welcome back.' : 'Ship your bio link in 90 seconds.'}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[var(--color-on-surface-variant)]">
            {session
              ? 'Your dashboard is one click away.'
              : 'Free forever for your first link. Custom domain included. No credit card. No fake "starter" tier that breaks at 100 visitors.'}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {isPending ? null : session ? (
              <ButtonLink to="/app" variant="primary" size="lg">
                Go to dashboard
                <ArrowRight size={18} />
              </ButtonLink>
            ) : (
              <>
                <ButtonLink to="/signup" variant="primary" size="lg">
                  Start free
                  <ArrowRight size={18} />
                </ButtonLink>
                <ButtonLink to="/login" variant="ghost" size="lg">
                  Sign in
                </ButtonLink>
              </>
            )}
          </div>
          {!session && (
            <div className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-[var(--color-on-surface-variant)]">
              <TrustItem>No credit card</TrustItem>
              <TrustItem>Free forever for 1 link</TrustItem>
              <TrustItem>GDPR-compliant CAPI</TrustItem>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function TrustItem({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Check size={12} className="text-[#7dffa2]" />
      <span>{children}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Footer.
// ---------------------------------------------------------------------------

function Footer() {
  const { data: session } = useSession();
  return (
    <footer className="relative z-10 mx-auto max-w-6xl px-6 pb-12">
      <div className="flex flex-wrap items-center justify-between gap-6 border-t border-[var(--color-border)] pt-8 text-sm text-[var(--color-on-surface-variant)]">
        <Wordmark to="/" size="sm" />
        <div className="flex flex-wrap items-center gap-5">
          {session ? (
            <RouterLink to="/app" className="transition hover:text-[var(--color-foreground)]">
              Dashboard
            </RouterLink>
          ) : (
            <>
              <RouterLink to="/login" className="transition hover:text-[var(--color-foreground)]">
                Sign in
              </RouterLink>
              <RouterLink to="/signup" className="transition hover:text-[var(--color-foreground)]">
                Sign up
              </RouterLink>
            </>
          )}
          <a href="mailto:hello@poof.bio" className="transition hover:text-[var(--color-foreground)]">
            hello@poof.bio
          </a>
        </div>
      </div>
      <p className="mt-6 text-xs text-[var(--color-on-surface-variant)] opacity-60">
        © 2026 Poof. Built for creators who refuse to bleed conversions to
        Meta&rsquo;s in-app browser.
      </p>
    </footer>
  );
}

// ---------------------------------------------------------------------------
// Shared section + feature components.
// ---------------------------------------------------------------------------

function Section({
  anchor,
  eyebrow,
  title,
  subtitle,
  children,
}: {
  anchor?: string;
  eyebrow: React.ReactNode;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      id={anchor}
      className="relative z-10 mx-auto max-w-6xl px-6 py-12 md:py-16"
    >
      <div className="mb-8 max-w-3xl">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] bg-[var(--surface-glass)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-primary)] backdrop-blur">
          {eyebrow}
        </div>
        <h2
          className="mt-5 font-display font-extrabold tracking-[-0.03em]"
          style={{ fontSize: 'clamp(1.75rem, 3.5vw, 3rem)' }}
        >
          {title}
        </h2>
        <p className="mt-4 text-pretty text-base leading-relaxed text-[var(--color-on-surface-variant)] md:text-lg">
          {subtitle}
        </p>
      </div>
      {children}
    </section>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div
      className="flex gap-4 rounded-2xl border border-[var(--color-border)] p-5 transition hover:border-[var(--color-primary)]/30"
      style={{ background: 'var(--surface-glass)' }}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[var(--color-primary)]"
        style={{
          background:
            'linear-gradient(135deg, rgba(124, 77, 255, 0.25) 0%, rgba(124, 77, 255, 0.08) 100%)',
        }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-display text-base font-bold tracking-tight">{title}</div>
        <p className="mt-1 text-sm leading-relaxed text-[var(--color-on-surface-variant)]">
          {description}
        </p>
      </div>
    </div>
  );
}
